/**
 * ResourceUploadDialog - Modal for uploading new resources
 */
import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Upload, Plus, Check, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import { 
  PROGRAMS, 
  LIBRARIES, 
  RESOURCE_TYPES, 
  INITIAL_FORM,
  formatFileSize 
} from "./ResourceConstants";

export function ResourceUploadDialog({ token, onUploadSuccess }) {
  const [isOpen, setIsOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadForm, setUploadForm] = useState(INITIAL_FORM);
  const [modules, setModules] = useState([]);
  const [lessons, setLessons] = useState([]);

  const fetchModules = useCallback(async (program) => {
    if (!program) {
      setModules([]);
      setLessons([]);
      return;
    }
    
    const courseMap = {
      wake_up: "course_wake_up",
      burn_the_map: "course_burn_map",
      whole: "course_whole",
    };
    const courseId = courseMap[program];
    
    if (!courseId) {
      setModules([]);
      setLessons([]);
      return;
    }
    
    try {
      const res = await axios.get(`${API}/admin/modules`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { course_id: courseId }
      });
      setModules(res.data || []);
    } catch (error) {
      console.error("Failed to fetch modules:", error);
      setModules([]);
    }
  }, [token]);

  const fetchLessons = useCallback(async (moduleId) => {
    if (!moduleId) {
      setLessons([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/admin/lessons`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { module_id: moduleId }
      });
      setLessons(res.data || []);
    } catch (error) {
      console.error("Failed to fetch lessons:", error);
      setLessons([]);
    }
  }, [token]);

  useEffect(() => {
    fetchModules(uploadForm.program);
    if (uploadForm.program && !uploadForm.vault_chamber) {
      const matchingLibrary = LIBRARIES.find(l => l.program === uploadForm.program);
      if (matchingLibrary) {
        setUploadForm(prev => ({ ...prev, vault_chamber: matchingLibrary.value }));
      }
    }
  }, [uploadForm.program, uploadForm.vault_chamber, fetchModules]);

  useEffect(() => {
    fetchLessons(uploadForm.module_id);
  }, [uploadForm.module_id, fetchLessons]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 50 * 1024 * 1024) {
        toast.error("File size exceeds 50MB limit");
        return;
      }
      setUploadForm(prev => ({ 
        ...prev, 
        file, 
        title: prev.title || file.name.replace(/\.[^/.]+$/, "") 
      }));
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file) {
      toast.error("Please select a file");
      return;
    }
    if (!uploadForm.title.trim()) {
      toast.error("Please enter a title");
      return;
    }
    if (!uploadForm.vault_chamber) {
      toast.error("Please select a library/collection");
      return;
    }
    if (!uploadForm.program) {
      toast.error("Please select a program");
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append("file", uploadForm.file);

      const params = new URLSearchParams({
        title: uploadForm.title.trim(),
        description: uploadForm.description || "",
        resource_type: uploadForm.resource_type,
        vault_chamber: uploadForm.vault_chamber,
        program: uploadForm.program,
      });
      
      if (uploadForm.module_id) params.append("module_id", uploadForm.module_id);
      if (uploadForm.lesson_id) params.append("lesson_id", uploadForm.lesson_id);

      await axios.post(`${API}/admin/resources/upload?${params.toString()}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        },
      });

      toast.success("Resource uploaded successfully!");
      setIsOpen(false);
      setUploadForm(INITIAL_FORM);
      onUploadSuccess?.();
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error(error.response?.data?.detail || "Failed to upload resource");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button className="btn-primary" data-testid="upload-resource-btn">
          <Plus className="w-4 h-4 mr-2" />
          Upload Resource
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-emerald-900 border-white/10 text-white max-w-lg !p-0 !gap-0 flex flex-col">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-white/10 shrink-0">
          <DialogTitle className="text-white text-xl">Upload New Resource</DialogTitle>
          <DialogDescription className="text-white/60">
            Add a PDF or document to appear in the Vault library
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleUpload} className="flex flex-col flex-1 min-h-0 overflow-hidden">
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            {/* File Input */}
            <div>
              <Label className="text-white/80 font-medium text-sm">PDF File (Max 50MB) *</Label>
              <div className="mt-1.5 border-2 border-dashed border-white/20 rounded-lg p-3 text-center hover:border-primary/50 transition-colors cursor-pointer">
                <input
                  type="file"
                  onChange={handleFileChange}
                  className="hidden"
                  id="file-upload"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.mp3,.mp4,.wav"
                  data-testid="file-input"
                />
                <label htmlFor="file-upload" className="cursor-pointer block">
                  <Upload className="w-6 h-6 mx-auto text-white/40 mb-1" />
                  {uploadForm.file ? (
                    <div>
                      <p className="text-white font-medium text-sm">{uploadForm.file.name}</p>
                      <p className="text-white/50 text-xs">{formatFileSize(uploadForm.file.size)}</p>
                    </div>
                  ) : (
                    <p className="text-white/60 text-sm">Click to select or drag and drop</p>
                  )}
                </label>
              </div>
            </div>

            {/* Title */}
            <div>
              <Label className="text-white/80 font-medium text-sm">Title *</Label>
              <Input
                value={uploadForm.title}
                onChange={(e) => setUploadForm(prev => ({ ...prev, title: e.target.value }))}
                className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-white/40 h-9"
                placeholder="Resource title"
                required
                data-testid="title-input"
              />
            </div>

            {/* Description */}
            <div>
              <Label className="text-white/80 font-medium text-sm">Short Description</Label>
              <Textarea
                value={uploadForm.description}
                onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                className="mt-1 bg-white/10 border-white/20 text-white min-h-[50px] placeholder:text-white/40"
                placeholder="Brief description shown on the resource card"
                data-testid="description-input"
              />
            </div>

            {/* Program and Library */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white/80 font-medium text-sm">Program *</Label>
                <Select 
                  value={uploadForm.program}
                  onValueChange={(v) => setUploadForm(prev => ({ 
                    ...prev, 
                    program: v,
                    module_id: "",
                    lesson_id: "",
                  }))}
                >
                  <SelectTrigger className="mt-1 bg-white/10 border-white/20 text-white h-9" data-testid="program-select">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent className="bg-emerald-900 border-white/20">
                    {PROGRAMS.map(prog => (
                      <SelectItem key={prog.value} value={prog.value} className="text-white hover:bg-white/10">
                        <span className="flex items-center gap-2">
                          <prog.icon className="w-3 h-3" style={{ color: prog.color }} />
                          {prog.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-white/80 font-medium text-sm">Library *</Label>
                <Select 
                  value={uploadForm.vault_chamber}
                  onValueChange={(v) => setUploadForm(prev => ({ ...prev, vault_chamber: v }))}
                >
                  <SelectTrigger className="mt-1 bg-white/10 border-white/20 text-white h-9" data-testid="library-select">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent className="bg-emerald-900 border-white/20">
                    {LIBRARIES.map(lib => (
                      <SelectItem key={lib.value} value={lib.value} className="text-white hover:bg-white/10">
                        {lib.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Resource Type */}
            <div>
              <Label className="text-white/80 font-medium text-sm">Resource Type</Label>
              <Select 
                value={uploadForm.resource_type}
                onValueChange={(v) => setUploadForm(prev => ({ ...prev, resource_type: v }))}
              >
                <SelectTrigger className="mt-1 bg-white/10 border-white/20 text-white h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-emerald-900 border-white/20">
                  {RESOURCE_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value} className="text-white hover:bg-white/10">
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Module Link */}
            {modules.length > 0 && (
              <div>
                <Label className="text-white/80 font-medium text-sm">Link to Module (Optional)</Label>
                <Select 
                  value={uploadForm.module_id || "none"}
                  onValueChange={(v) => setUploadForm(prev => ({ 
                    ...prev, 
                    module_id: v === "none" ? "" : v,
                    lesson_id: ""
                  }))}
                >
                  <SelectTrigger className="mt-1 bg-white/10 border-white/20 text-white h-9">
                    <SelectValue placeholder="Select module" />
                  </SelectTrigger>
                  <SelectContent className="bg-emerald-900 border-white/20">
                    <SelectItem value="none" className="text-white/60 hover:bg-white/10">None</SelectItem>
                    {modules.map(module => (
                      <SelectItem key={module.module_id} value={module.module_id} className="text-white hover:bg-white/10">
                        {module.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Lesson Link */}
            {lessons.length > 0 && uploadForm.module_id && (
              <div>
                <Label className="text-white/80 font-medium text-sm">Link to Lesson (Optional)</Label>
                <Select 
                  value={uploadForm.lesson_id || "none"}
                  onValueChange={(v) => setUploadForm(prev => ({ ...prev, lesson_id: v === "none" ? "" : v }))}
                >
                  <SelectTrigger className="mt-1 bg-white/10 border-white/20 text-white h-9">
                    <SelectValue placeholder="Select lesson" />
                  </SelectTrigger>
                  <SelectContent className="bg-emerald-900 border-white/20">
                    <SelectItem value="none" className="text-white/60 hover:bg-white/10">None</SelectItem>
                    {lessons.map(lesson => (
                      <SelectItem key={lesson.lesson_id} value={lesson.lesson_id} className="text-white hover:bg-white/10">
                        {lesson.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Upload Progress */}
            {uploading && (
              <div className="space-y-1">
                <div className="w-full bg-white/10 rounded-full h-1.5">
                  <div 
                    className="bg-primary h-1.5 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <p className="text-xs text-white/60 text-center">Uploading... {uploadProgress}%</p>
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t border-white/10 bg-emerald-900 shrink-0">
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                className="flex-1 border-white/20 text-white hover:bg-white/10 h-9"
                onClick={() => {
                  setIsOpen(false);
                  setUploadForm(INITIAL_FORM);
                }}
                disabled={uploading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 btn-primary h-9"
                disabled={uploading || !uploadForm.file}
                data-testid="submit-upload-btn"
              >
                {uploading ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Uploading</>
                ) : (
                  <><Check className="w-4 h-4 mr-2" /> Upload</>
                )}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
